import { Button } from "@/components/ui/button.tsx";
import { Checkbox } from "@/components/ui/checkbox";
import { UserAvatar } from "@/components/ui/stacked-avatars";
import { useToast } from "@/components/ui/use-toast";
import { SupervisorMentor } from "@/lib/api/user.ts";
import { useUnassignSupervisorMentor } from "@/lib/hooks/mentors/use-unassign-supervisor-mentors";
import { ColumnDef } from "@tanstack/react-table";
import { Trash2Icon } from "lucide-react";
import { useState } from "react";
import { ConfirmUnassignDialog } from "./confirm-unassign-dialog";

export const getColumns = (
  userId: string,
  canUnassignSupervisor: boolean,
  mentorCount: number,
): ColumnDef<SupervisorMentor>[] => {
  const { toast } = useToast();

  return [
    ...(canUnassignSupervisor
      ? [
          {
            id: "select",
            header: ({ table }) => (
              <div onClick={(event) => event.stopPropagation()}>
                <Checkbox
                  checked={table.getIsAllPageRowsSelected()}
                  onCheckedChange={(value) =>
                    table.toggleAllPageRowsSelected(!!value)
                  }
                  aria-label="Select all"
                />
              </div>
            ),
            cell: ({ row }) => (
              <div onClick={(event) => event.stopPropagation()}>
                <Checkbox
                  checked={row.getIsSelected()}
                  onCheckedChange={(value) => row.toggleSelected(!!value)}
                  aria-label="Select row"
                />
              </div>
            ),
            enableSorting: false,
            enableHiding: false,
          } satisfies ColumnDef<SupervisorMentor>,
        ]
      : []),
    {
      accessorKey: "mentor_name",
      header: `Mentor (${mentorCount})`,
      cell: ({ row }) => {
        const mentor = row.original;
        return (
          <div className="flex items-center">
            <UserAvatar
              name={mentor.mentor_name}
              imageUrl={mentor.mentor_profile_picture_url || undefined}
              size={32}
              showBorder={false}
            />
            <div className="ml-2 flex flex-1 flex-col gap-1 overflow-hidden">
              <div className="inline-flex items-center justify-between text-base font-bold leading-tight text-black hover:underline">
                {mentor.mentor_name}
              </div>
            </div>
          </div>
        );
      },
    },
    ...(canUnassignSupervisor
      ? [
          {
            id: "actions",
            cell: ({ row }) => {
              const mentor = row.original;
              const [isConfirmOpen, setIsConfirmOpen] = useState(false);
              const { mutate: unassignMentor, isPending } =
                useUnassignSupervisorMentor();

              const handleUnassign = () => {
                unassignMentor(
                  { mentorId: mentor.mentor_id, supervisorId: userId },
                  {
                    onSuccess: () => {
                      toast({
                        title: "Mentor Unassigned",
                        description:
                          "The mentor has been successfully unassigned.",
                      });
                      setIsConfirmOpen(false);
                    },
                    onError: () => {
                      toast({
                        title: "Error",
                        description:
                          "Failed to unassign mentorship. Please try again.",
                        variant: "destructive",
                      });
                    },
                  },
                );
              };

              return (
                <div onClick={(event) => event.stopPropagation()}>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setIsConfirmOpen(true)}
                    disabled={isPending}
                  >
                    <Trash2Icon className="mr-2 h-4 w-4" />
                    Unassign
                  </Button>
                  <ConfirmUnassignDialog
                    isOpen={isConfirmOpen}
                    onClose={() => setIsConfirmOpen(false)}
                    onConfirm={handleUnassign}
                    isPending={isPending}
                  />
                </div>
              );
            },
          } satisfies ColumnDef<SupervisorMentor>,
        ]
      : []),
  ];
};
